import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import { v4 as uuidv4 } from "uuid";
import POSPageGridCard from "../gridCards/POSPageGridCard";
import * as Library from "../../../utils/Library";
import moment from "moment";
import { useNavigate } from "react-router";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;
/*  #endregion */

/**
 * Renders an infinite grid of brand cards.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The additional CSS class name for the component.
 * @param {number} [props.limit=10] - The maximum number of cards to load per page.
 * @param {string} [props.queryKey] - The query key for fetching data.
 * @param {number} [props.minimumCardsFill] - The minimum number of cards to fill the grid.
 * @param {number} [props.minColumnWidth] - The minimum width of each grid column.
 * @param {Function} [props.onEditAction] - The callback function for the edit action.
 * @param {Function} [props.onDeleteAction] - The callback function for the delete action.
 * @param {Function} [props.onSuspendAction] - The callback function for the suspend action.
 * @param {string} [props.statusFromPath] - The status from the path.
 * @returns {JSX.Element} The rendered component.
 */

export default function InfinityPOSPageGridCard(props) {
    const pageLimit = props?.limit || 10;

    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${props?.minColumnWidth || "395px"}, 1fr))`,
        gap: "24px"
    };

    /* #region STATES */
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);

    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit
    const requestEnabled = props?.requestEnabled ?? true;

    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());

    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    const filters = props?.filters;

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getPOSsData",
        {
            companyID: localStorage.getItem("currentCompanyID"),
            limit: pageLimit,
            ...filters
        },
        () => {
            setScroll(true);
        },
        false,
        "",
        false,
        requestEnabled
    );

    const pageList = data?.pages;
    //const totalCount = data?.pages[0]?.data?.totalCount;
    const nextDataCount = data?.pages[data?.pages?.length - 1].data?.nextDataCount;
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);
    /* #endregion */

    /* #region METHODS */
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const isAtBottom =
            Math.floor(scrollContainer.scrollHeight - scrollContainer.scrollTop) <= scrollContainer.clientHeight;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const formatIsoToDateTime = isoDate => {
        if (!isoDate) return "--";
        const res = moment(isoDate).format("DD/MM/YYYY HH:mm");
        if (res === "Invalid date") return "--";
        return res;
    };

    /* #endregion */

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: 8 }, () => (
                        <CardGridSkeleton
                            key={uuidv4()}
                            height="420.28px"
                            minColumnWidth={props?.minColumnWidth || "439px"}
                        />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {pageList?.map(it => {
                        return it?.data?.result?.map(item => {
                            const status = item?.status;

                            return (
                                <POSPageGridCard
                                    key={item?.ourID}
                                    image={item?.posLogo?.mainLogoURL || ""}
                                    //imageOverlay={item?.retailBrand?.logos?.mainLogoURL || ""}
                                    //isImageWithOverlay={true}
                                    title={item?.posName || "-"}
                                    titlePrefix="POS NAME"
                                    id={item?.ourID || "--"}
                                    idPrefix="BOTAG ID"
                                    isCardEnabled={true}
                                    isCardSuspended={false}
                                    data={{
                                        createdBy: item?.createdBy,
                                        createdOn: formatIsoToDateTime(item?.createdOn),
                                        companyName: item?.companyName,
                                        supportEmail: item?.supportEmail?.[0],
                                        productsImport: item?.productsImport,
                                        supplierOrdersImport: item?.supplierOrdersImport,
                                        productsExport: item?.productsExport,
                                        supplierOrdersExport: item?.supplierOrdersExport,
                                        stockImport: item?.stockImport,
                                        pricesImport: item?.pricesImport,
                                        customerOrders: item?.customerOrders,
                                        status: status
                                    }}
                                    edit={{
                                        hidden: false,
                                        active: true,
                                        onClick: () => navigate(`/pos/edit/${item?._id}`)
                                    }}
                                    suspend={{ isReenable: false, hidden: false, active: false }}
                                    delete={{ active: false, hidden: false }}
                                    primaryButton={{
                                        title: props?.primaryButton?.title ?? "SET AS DEFAULT",
                                        onClick:
                                            props?.primaryButton?.onClick && !item?.default
                                                ? () => props?.primaryButton?.onClick(item)
                                                : undefined,
                                        isDisabled: props?.primaryButton?.isDisabled
                                    }}
                                    secondaryButton={{ title: "DETAILS" }}
                                    highlighted={item?.default ? true : false}
                                    highlightColor={item?.default ? "#0060cc" : ""}
                                    highlightCustomText={"DEFAULT"}
                                />
                            );
                        });
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <POSPageGridCard
                                key={index}
                                isCardEnabled={false}
                                primaryButton={{ title: "SET AS DEFAULT" }}
                                secondaryButton={{ title: "DETAILS" }}
                                idPrefix="BOTAG ID"
                                titlePrefix="POS NAME"
                            />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton key={index} height="420.28px" />
                        ))}
                </div>
            )}
        </StyledPage>
    );
}
